// The cheer sheets on the web (docs/cheers.md "The cheer sheet", "The doer's sheet"). Owner,
// 2026-10-28, of the first cut's quiet word on the task screen: "it should not be a toggle at all and
// it should not be quiet and hidden in the task screen". So the cheering comes to the person: once the
// open board has loaded, a job someone else finished that this peon has not cheered comes up in a sheet
// with a gold Cheer! button, and a peon whose job was cheered gets a sheet of their own.
//
// The rule is shared/cheers.ts cheerSheets; this module keeps the device's bookmark (`op.cheerSeen`),
// reads GET /api/cheers/news once per page load with cheersUI on (and again when the page comes back
// to the front after a while), and hands the shell what to show. The doer's sheet comes first (it is
// about you), then the jobs, one at a time, newest first.
import { useEffect, useState } from "preact/hooks";
import { CHEER_SEEN_KEY, cheerSheets, laterInstant, parseCheerBookmark, type CheerBookmark, type CheerNews, type CheeredJob } from "../shared/cheers.ts";
import type { Task } from "../shared/types";

/** Kept when storage refuses, so a private window does not show a sheet twice in one open. */
let memory: CheerBookmark | null = null;

export function cheerBookmark(): CheerBookmark {
  try {
    const raw = localStorage.getItem(CHEER_SEEN_KEY);
    return raw ? parseCheerBookmark(raw) : (memory ?? { jobs: null, cheered: null });
  } catch {
    return memory ?? { jobs: null, cheered: null };
  }
}

function saveBookmark(next: CheerBookmark): void {
  memory = next;
  try {
    localStorage.setItem(CHEER_SEEN_KEY, JSON.stringify(next));
  } catch {
    /* ignore: the copy in memory holds for this open */
  }
}

/** A read this old or older is made again when the page comes back to the front. */
const READ_AGAIN_MS = 5 * 60_000;

let reading = false;
let readAt = 0;
let loader: (() => Promise<CheerNews<Task>>) | null = null;
let boardLoaded = false;
let jobs: Task[] = [];
let jobsSeen: string | null = null;
let cheered: CheeredJob[] = [];
let cheeredSeen: string | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of listeners) fn();
}

function take(news: CheerNews<Task>): void {
  const seen = cheerBookmark();
  const next = cheerSheets(news, seen);
  // Jobs already queued and not yet closed stay; a newer read only adds.
  const queued = new Set(jobs.map((t) => t.id));
  jobs = [...jobs, ...next.jobs.filter((t) => !queued.has(t.id))];
  jobsSeen = laterInstant(jobsSeen, next.jobsSeen);
  if (next.cheered.length > 0) cheered = next.cheered;
  cheeredSeen = laterInstant(cheeredSeen, next.cheeredSeen);
  emit();
}

function read(): void {
  if (!loader || reading) return;
  reading = true;
  readAt = Date.now();
  loader()
    .then(take, () => {
      /* offline: nothing to show this time */
    })
    .finally(() => {
      reading = false;
    });
}

function onVisible(): void {
  if (document.visibilityState === "visible" && Date.now() - readAt >= READ_AGAIN_MS) read();
}

/** Signed in with cheersUI on: read the news now, and again when the page comes back to the front after a while. */
export function startCheers(load: () => Promise<CheerNews<Task>>): void {
  const first = loader === null;
  loader = load;
  if (first) {
    read();
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisible);
  }
}

/** The open board has its list (src/views/Board.tsx): the sheets may show from now on. */
export function cheerBoardLoaded(): void {
  if (boardLoaded) return;
  boardLoaded = true;
  emit();
}

/** The job on the cheer sheet now (the newest not yet closed), once the board has loaded; null for none. */
export function pendingCheerJob(): Task | null {
  return boardLoaded ? (jobs[0] ?? null) : null;
}

/** The doer's sheet's jobs, once the board has loaded; [] for none. */
export function pendingCheered(): CheeredJob[] {
  return boardLoaded ? cheered : [];
}

/** The job up now was cheered or passed: the next comes up; after the last, the bookmark moves past them all. */
export function nextCheerJob(): void {
  jobs = jobs.slice(1);
  if (jobs.length === 0) saveBookmark({ ...cheerBookmark(), jobs: laterInstant(cheerBookmark().jobs, jobsSeen) });
  emit();
}

/** Later: every job on the sheet is passed by on this device. */
export function closeCheerJobs(): void {
  jobs = [];
  saveBookmark({ ...cheerBookmark(), jobs: laterInstant(cheerBookmark().jobs, jobsSeen) });
  emit();
}

/** The doer's sheet closed: its cheers are bookmarked. */
export function closeCheered(): void {
  cheered = [];
  saveBookmark({ ...cheerBookmark(), cheered: laterInstant(cheerBookmark().cheered, cheeredSeen) });
  emit();
}

/** The workshop's "Show the cheer sheets now": shows what the news holds now, ignoring the bookmark (closing still moves it). */
export function previewCheers(news: CheerNews<Task>): void {
  boardLoaded = true;
  const next = cheerSheets(news, { jobs: null, cheered: null });
  jobs = next.jobs;
  jobsSeen = laterInstant(jobsSeen, next.jobsSeen);
  cheered = next.cheered;
  cheeredSeen = laterInstant(cheeredSeen, next.cheeredSeen);
  emit();
}

export interface CheerSheetState {
  job: Task | null;
  /** How many jobs are queued behind the one up now. */
  more: number;
  cheered: CheeredJob[];
}

function state(): CheerSheetState {
  return { job: pendingCheerJob(), more: Math.max(0, (boardLoaded ? jobs.length : 0) - 1), cheered: pendingCheered() };
}

/** The sheets' state for the shell. */
export function useCheerSheets(): CheerSheetState {
  const [s, setS] = useState(state);
  useEffect(() => {
    const update = () =>
      setS((prev) => {
        const next = state();
        return prev.job === next.job && prev.more === next.more && prev.cheered === next.cheered ? prev : next;
      });
    listeners.add(update);
    update();
    return () => {
      listeners.delete(update);
    };
  }, []);
  return s;
}

/** Tests only. */
export function resetCheersForTests(): void {
  reading = false;
  readAt = 0;
  loader = null;
  boardLoaded = false;
  jobs = [];
  jobsSeen = null;
  cheered = [];
  cheeredSeen = null;
  memory = null;
  listeners.clear();
}
